<template>
  <div class="unlock-sessions">
    <button class="unlock-sessions__to-login btn btn-light" title="Ir al incio de sesión" @click="closeUnlockSessions">
      <i class="fad fa-sign-in-alt"></i>
    </button>
    <img class="bm-logo" src="@/assets/img/bm-logo-color.png"/>
    <slot v-if="!sessionDeleted && hasUserSessions" name="unlock-sessions__error-message"></slot>
    <p v-if="hasUserSessions" class="unlock-sessions__info">Has sobrepasado tu límite de licencias, por lo cual, es necesario liberar al menos
      una de ellas para poder iniciar sesión.</p>
    <div class="unlock-sessions__sessions">
      <SessionCard v-for="(session, index) in userSessions" :key="index" :session="session" :isMobile="false"
        :userCredentials="userCredentials"
        @remove-session="removeSession"
      >
      </SessionCard>
    </div>
    <div v-show="sessionDeleted || !hasUserSessions" class="invite-login alert alert-success">
      <span class="invite-login__message small">
        <p v-if="!hasUserSessions" class="m-0">¡Bien!, no cuentas con ninguna sesión iniciada.</p>
        <p class="m-0">Ahora puedes intentar el inicio de sesión nuevamente.</p>
      </span>
      <button class="unlock-sessions__to-login btn btn-light" @click="closeUnlockSessions"><i class="fad fa-sign-in-alt"></i></button>
    </div>
  </div>
</template>

<script lang="ts">
import AuthService from '@/services/support/AuthService'
import LogService, { ToString } from '@/services/support/LogService'
import { License } from '@/types/catalogs/Licenses'
import Vue from 'vue'
import SessionCard from './SessionCard.vue'

export default Vue.extend({
  components: {
    SessionCard
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    username: {
      type: String,
      required: true
    },
    password: {
      type: String,
      required: true
    },
    loadSessions: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data () {
    return {
      inviteToLoginMessage: false as boolean,
      userSessions: [] as Array<License>,
      sessionDeleted: false as boolean
    }
  },
  computed: {
    userCredentials (): { username: string, password: string } {
      return {
        username: `${this.username}@${this.domain}`,
        password: this.password
      }
    },
    hasUserSessions (): boolean {
      return this.userSessions.length > 0
    }
  },
  watch: {
    loadSessions (newValue: boolean, oldValue: boolean): void {
      if (!oldValue && newValue) this.getSessions()
    }
  },
  methods: {
    /**
     * Emit the event to close this component
     */
    closeUnlockSessions (): void {
      this.setSessionDeleted(false)
      this.$emit('close-unlock-sessions')
    },
    /**
     * Get the request to obtain all the sessions belong to the user.
     */
    getSessions (): void {
      const credentials = this.userCredentials
      AuthService.getUserSessions(credentials)
        .then((response) => {
          this.userSessions = response.results
          this.setSessionDeleted(false)
        })
        .catch((error) => {
          LogService.error(`Error al obtener las sesiones: ${ToString(error.response ?? error)}`)
        })
    },
    /**
     * Set the 'value' to the 'sessionDeleted' variable
     *
     * @param value to set
     */
    setSessionDeleted (value: boolean): void {
      this.sessionDeleted = value
    },
    /**
     * Get the index from the current user sessions list
     *
     * @param sessionId id of the session to get
     */
    indexSessionFromList (sessionId: number): number {
      return this.userSessions.findIndex((sess) => sess.id === sessionId)
    },
    /**
     * Removes one session identified by its id.
     *
     * @param sessionId id of the session to remove from the current list
     */
    removeSession (sessionId: number): void {
      const sessinIndex = this.indexSessionFromList(sessionId)
      if (sessinIndex !== -1) {
        this.userSessions.splice(sessinIndex, 1)
        this.setSessionDeleted(true)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
body.dev .bm-logo,
body.stg .bm-logo {
  filter: grayscale(100%);
  height: 1.5rem;
  margin: 1rem 2rem;
  position: absolute;
  top: 0;
  right: 0;
}
.unlock-sessions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 56px 2rem 2rem 2rem;
  border-radius: 0 12px 12px 0;
  background-color: var(--white);

  .unlock-sessions__to-login {
    position: absolute;
    top: 0px;
    left: 0px;
    padding: 16px;
    margin: 0;
    border: 0;
    border-radius: 0 0 4px 0;
  }

  .unlock-sessions__info {
    font-size: 14px;
    margin: 12px 0;
    padding: 4px;
    color: var(--dark);
  }

  .unlock-sessions__sessions {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-y: auto;
    border-radius: 4px;
    /* width */
    &::-webkit-scrollbar {
      width: 5px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  .invite-login.alert.alert-success {
    display: flex;
    justify-content: space-between;
    padding: 0;
    margin: 1rem 0;
    .invite-login__message {
      padding: 1rem;
    }
    .unlock-sessions__to-login {
      position: relative;
      height: 100%;
    }
  }
}
</style>
